import { useEffect } from 'react';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';

import Wrapper from 'components/layouts/Wrapper/Wrapper';
import { WeeklyPayouts } from '../WeeklyPayouts/WeeklyPayouts';
import ListEmpty from 'components/modules/Common/ListEmpty/ListEmpty';

import {
  BalanceStatsBar,
  LoadingSpinner,
  ToolBar,
  Typhography,
  SelectBox,
  Headers,
} from 'components/core';

import { balanceWeekTabnavData } from 'constants/TabNavData';
import { addDaysFunction, getCurrentWeekStartandEndDates } from 'shared/utilities/dataConversion';
import { pastDropdownItemsWeek } from 'constants/DateRangeDropDownDatas';

import { getURLParams, useDateRangeSorting } from 'hooks/useDateRangeSorting';
import { useCurrencyFormat } from 'hooks/useCurrencyFormat';

import {
  useGetPastBalanceHeroStatsQuery,
  useGetPendingBalanceApiQuery,
} from 'services/pastBalanceApi';

const PastWeekContainer = () => {
  const [
    rangeSelector,
    defaultSortingText,
    itemSlectedState,
    showDropdown,
    handlerSortingDropDownClick,
    handlerSortingOption,
  ]: any = useDateRangeSorting('All', pastDropdownItemsWeek);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    navigate(
      {
        pathname: location.pathname,
        search: `?${createSearchParams(getURLParams(defaultSortingText))}`,
      },
      { replace: true }
    );
  }, [defaultSortingText]);

  const [wkStart] = getCurrentWeekStartandEndDates(
    moment().tz('America/Chicago').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).format()
  );

  const setLessThanDate = moment(wkStart)
    .tz('America/Chicago')
    .subtract(1, 'day')
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .format();
  const setGreaterThan =
    rangeSelector > 0
      ? moment()
          .tz('America/Chicago')
          .subtract(rangeSelector, 'day')
          .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
          .format()
      : undefined;

  /*
   * List the Past Invoices
   */
  const invoiceParams = {
    first: 3000,
    offset: 0,
    filter: {
      status: { in: ['INVOICED', 'APPROVED', 'PAID'] },
      customer_payment_due_at: {
        lessThan: setLessThanDate,
        greaterThan: setGreaterThan,
      },
    },
    groupBy: 'CUSTOMER_PAYMENT_DUE_AT_TRUNCATED_TO_WEEK_CST',
    orderGroupBy: 'DESC',
  };

  const { data, isLoading, isFetching }: any = useGetPendingBalanceApiQuery(invoiceParams);

  /*
   * Fetch the Past Invoices Hero Stats data
   */
  const pastHeroStatsParams = {
    filter: {
      status: { in: ['APPROVED', 'PAID', 'INVOICED'] },
      customer_payment_due_at: {
        lessThan: setLessThanDate,
        greaterThan: setGreaterThan,
      },
    },
    timeCycleDays: '' + rangeSelector + '',
  };

  const { data: pastBalanceStatsBarData, isSuccess: isSuccessPastBalanceStatsBar }: any =
    useGetPastBalanceHeroStatsQuery(pastHeroStatsParams);

  return (
    <>
      {(isLoading || isFetching) && <LoadingSpinner />}
      <Wrapper additionalClasses="flex-col items-start" pageTitle="Balance">
        <Headers headingContent="Balance" />
        {!isLoading && (
          <>
            <ToolBar navigationData={balanceWeekTabnavData} isShowOptionEnabled={false}>
              <SelectBox
                itemClickHandler={handlerSortingOption}
                defaultSelectBoxText={defaultSortingText}
                itemSelected={itemSlectedState}
                dropDownState={showDropdown}
                dropDownClickHandler={handlerSortingDropDownClick}
                items={pastDropdownItemsWeek}
              />
            </ToolBar>

            {isSuccessPastBalanceStatsBar && (
              <BalanceStatsBar
                defaultSortingText={defaultSortingText}
                data={pastBalanceStatsBarData}
              />
            )}

            {data?.payouts?.groupedAggregates.length > 0 && (
              <div className="flex flex-col w-full">
                <Typhography
                  component="div"
                  type="h2"
                  color="text-grey-900"
                  additionalClasses="items-center uppercase pt-9 pb-3 border-t border-solid border-grey-100 px-4 !font-normal"
                >
                  {'Invoices List'}
                </Typhography>

                <div className="flex flex-col w-full invoices-list-wrapper">
                  {data?.payouts?.groupedAggregates.map((listItem: any) => {
                    const convertedPrice = parseFloat(listItem?.sum?.amount);

                    const setDate = moment(listItem.keys[0]).tz('America/Chicago');
                    const endOfWeekToText = addDaysFunction(setDate, 6);
                    const endOfWeek = addDaysFunction(setDate, 6);

                    return (
                      <div className="flex flex-col w-full mb-0" key={listItem.keys[0]}>
                        <WeeklyPayouts
                          weekStartDate={listItem.keys[0]}
                          weekEndDate={endOfWeek}
                          mainLabel="Invoice for"
                          subLabel={`${moment(listItem.keys[0])
                            .tz('America/Chicago')
                            .format('MM/DD/YYYY')} - ${moment(endOfWeekToText)
                            .tz('America/Chicago')
                            .format('MM/DD/YYYY')}`}
                          rightColContent={useCurrencyFormat(convertedPrice)}
                          isRemoveTopBorder={data?.payouts?.groupedAggregates.length === 1}
                          timeFrame={'past'}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {data?.payouts?.groupedAggregates.length === 0 && <ListEmpty />}
          </>
        )}
      </Wrapper>
    </>
  );
};

export default PastWeekContainer;
